// ConversationList (DESIGN §3.3.4): Gruppen mit Kopf 28, Zeilen 40, aktiv `card`; Hover zeigt „…“
// (Pin, Rename, Delete). Umbenennen inline, Löschen mit Bestätigung. Ab 50 Einträgen virtualisiert.
// Daten kommen vom Modul `ki` über die Plattform-Shell.
import { DotsThree, PencilSimple, PushPin, PushPinSlash, Trash } from '@phosphor-icons/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { AlertDialog, DropdownMenu } from 'radix-ui';
import { type KeyboardEvent, useMemo, useRef, useState } from 'react';
import { cn } from '../kern/cn';
import { RouterLink } from '../kern/Link';
import { type ConversationSummary, groupConversations } from './conversation-groups';

export type { ConversationSummary } from './conversation-groups';

export interface ConversationListProps {
  conversations: readonly ConversationSummary[];
  loading?: boolean;
  onRename?: (id: string, title: string) => void;
  onTogglePin?: (id: string) => void;
  onDelete?: (id: string) => void;
  /** Nach einem Klick (Drawer schließen). */
  onNavigate?: () => void;
  className?: string;
}

type Row = { kind: 'head'; label: string } | { kind: 'item'; item: ConversationSummary };

const VIRTUAL_FROM = 50;
const menuItem =
  'flex h-9 cursor-pointer items-center gap-2 rounded-lg px-3 text-button text-fg outline-none data-highlighted:bg-card-hover';

function ConversationRow({
  item,
  onRename,
  onTogglePin,
  onAskDelete,
  onNavigate,
}: {
  item: ConversationSummary;
  onRename?: (id: string, title: string) => void;
  onTogglePin?: (id: string) => void;
  onAskDelete?: (item: ConversationSummary) => void;
  onNavigate?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.title);
  const hasMenu = Boolean(onRename || onTogglePin || onAskDelete);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'F2' && onRename) {
      event.preventDefault();
      setDraft(item.title);
      setEditing(true);
    } else if (event.key === 'Delete' && onAskDelete) {
      event.preventDefault();
      onAskDelete(item);
    }
  };

  if (editing) {
    return (
      <input
        aria-label="Chat title"
        // eslint-disable-next-line jsx-a11y/no-autofocus -- Inline-Umbenennen: Fokus gehört ins Feld
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const title = draft.trim();
            if (title && title !== item.title) onRename?.(item.id, title);
            setEditing(false);
          } else if (e.key === 'Escape') {
            e.stopPropagation();
            setEditing(false);
          }
        }}
        className="h-8 w-full rounded-md bg-input px-2.5 text-label text-fg outline-none focus-visible:outline-2 focus-visible:outline-focus"
      />
    );
  }

  return (
    <div
      className={cn(
        'group relative flex h-row-s items-center rounded-md',
        item.active ? 'bg-card' : 'hover:bg-card-hover',
      )}
    >
      <RouterLink
        href={item.href}
        data-roving
        aria-current={item.active ? 'page' : undefined}
        onKeyDown={onKeyDown}
        onClick={onNavigate}
        className={cn(
          'flex h-full min-w-0 flex-1 items-center gap-2 rounded-md px-2.5 text-label outline-offset-[-2px]',
          item.active ? 'text-fg' : 'text-fg-secondary group-hover:text-fg',
          hasMenu && 'pr-9',
        )}
      >
        {item.streaming ? (
          <span
            aria-label="Answering"
            className="size-1.5 shrink-0 animate-live rounded-full bg-accent text-accent"
          />
        ) : null}
        <span className="truncate">{item.title}</span>
      </RouterLink>
      {hasMenu ? (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            aria-label={`More for ${item.title}`}
            className="absolute right-1 flex size-7 items-center justify-center rounded-full text-fg-secondary opacity-0 transition-opacity duration-(--bar-dur-fast) group-hover:opacity-100 hover:bg-pill-card hover:text-fg focus-visible:opacity-100 data-[state=open]:opacity-100"
          >
            <DotsThree size={18} weight="bold" aria-hidden />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="start"
              sideOffset={4}
              className="z-(--bar-z-popover) min-w-44 rounded-xl bg-overlay p-1.5 shadow-2 data-[state=open]:animate-pop-in"
            >
              {onTogglePin ? (
                <DropdownMenu.Item className={menuItem} onSelect={() => onTogglePin(item.id)}>
                  {item.pinned ? (
                    <PushPinSlash size={18} aria-hidden />
                  ) : (
                    <PushPin size={18} aria-hidden />
                  )}
                  {item.pinned ? 'Unpin' : 'Pin'}
                </DropdownMenu.Item>
              ) : null}
              {onRename ? (
                <DropdownMenu.Item
                  className={menuItem}
                  onSelect={() => {
                    setDraft(item.title);
                    setEditing(true);
                  }}
                >
                  <PencilSimple size={18} aria-hidden />
                  Rename
                </DropdownMenu.Item>
              ) : null}
              {onAskDelete ? (
                <DropdownMenu.Item
                  className={cn(menuItem, 'text-danger-fg')}
                  onSelect={() => onAskDelete(item)}
                >
                  <Trash size={18} aria-hidden />
                  Delete
                </DropdownMenu.Item>
              ) : null}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      ) : null}
    </div>
  );
}

export function ConversationList({
  conversations,
  loading,
  onRename,
  onTogglePin,
  onDelete,
  onNavigate,
  className,
}: ConversationListProps) {
  const [pendingDelete, setPendingDelete] = useState<ConversationSummary | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const rows = useMemo<Row[]>(
    () =>
      groupConversations(conversations).flatMap((g) => [
        { kind: 'head', label: g.label } as Row,
        ...g.items.map((item) => ({ kind: 'item', item }) as Row),
      ]),
    [conversations],
  );
  const virtual = rows.length > VIRTUAL_FROM;
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual ist bewusst nicht memoisierbar
  const virtualizer = useVirtualizer({
    count: virtual ? rows.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => (rows[index]?.kind === 'head' ? 36 : 42),
    overscan: 8,
  });

  const renderRow = (row: Row) =>
    row.kind === 'head' ? (
      <p className="flex h-7 items-end px-2.5 pb-1 text-chip text-fg-tertiary">{row.label}</p>
    ) : (
      <ConversationRow
        item={row.item}
        onRename={onRename}
        onTogglePin={onTogglePin}
        onAskDelete={onDelete ? setPendingDelete : undefined}
        onNavigate={onNavigate}
      />
    );

  return (
    <nav aria-label="Chats" className={cn('flex min-h-0 flex-col', className)}>
      {loading ? (
        <div aria-busy="true" className="flex flex-col gap-1 px-1 pt-2">
          {Array.from({ length: 8 }, (_, i) => (
            <span
              key={i}
              aria-hidden
              className="h-row-s animate-skeleton rounded-md bg-skeleton"
              style={{ opacity: 1 - i * 0.08 }}
            />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="px-2.5 pt-3 text-label text-fg-tertiary">Your chats will show up here.</p>
      ) : (
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-1 pb-2">
          {virtual ? (
            <ul className="relative" style={{ height: virtualizer.getTotalSize() }}>
              {virtualizer.getVirtualItems().map((v) => (
                <li
                  key={v.key}
                  data-index={v.index}
                  ref={virtualizer.measureElement}
                  className="absolute inset-x-0 pb-0.5"
                  style={{ transform: `translateY(${v.start}px)` }}
                >
                  {renderRow(rows[v.index]!)}
                </li>
              ))}
            </ul>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {rows.map((row, index) => (
                <li
                  key={row.kind === 'head' ? `h-${row.label}` : row.item.id}
                  className={row.kind === 'head' && index > 0 ? 'mt-3' : undefined}
                >
                  {renderRow(row)}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <AlertDialog.Root
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 z-(--bar-z-dialog) bg-scrim data-[state=open]:animate-fade-in" />
          <AlertDialog.Content className="fixed top-1/2 left-1/2 z-(--bar-z-dialog) w-[min(400px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-card p-6 shadow-3 data-[state=open]:animate-pop-in">
            <AlertDialog.Title className="text-title-detail text-fg">
              Delete chat?
            </AlertDialog.Title>
            <AlertDialog.Description className="mt-2 text-body text-fg-secondary">
              “{pendingDelete?.title}” will be deleted for good. This can’t be undone.
            </AlertDialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <AlertDialog.Cancel className="h-10 rounded-full bg-pill-card px-4 text-button text-fg hover:bg-pill-card-hover">
                Cancel
              </AlertDialog.Cancel>
              <AlertDialog.Action
                className="h-10 rounded-full bg-danger-solid px-4 text-button text-on-danger-solid"
                onClick={() => pendingDelete && onDelete?.(pendingDelete.id)}
              >
                Delete
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </nav>
  );
}
